import { GetMyReportsExecutionHistoryDto } from "@/Schemas/Reports/GetMyReportsExecutionHistoryDto";
import { AssemblyDescription } from "./AssemblyDescription";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { TextOverflow, formatDate, formatDuration } from "vig-ui-library";
import { ReportExecutionHistoryParameterDto } from "@/Schemas/Reports/ReportExecutionHistoryParameterDto";
import { ReportParameterTypeEnum, getParameterTypeLabel } from "@/Enums/ReportParameterType";

export type AssemblyDetailProps = {
    executedReport: GetMyReportsExecutionHistoryDto;
}

export const AssemblyDetail = ({ executedReport }: AssemblyDetailProps) => {
    const theme = useTheme();

    const { executionStartTime, executionEndTime } = executedReport;

    const getExecutionDuration = (): string => {
        if (!executionStartTime || !executionEndTime) return "";

        const startDate = new Date(executionStartTime);
        const endDate = new Date(executionEndTime);

        const duration = endDate.getTime() - startDate.getTime();

        const seconds = Math.floor(duration / 1000);

        return formatDuration(seconds);
    }

    return (
        <Box sx={{ padding: 2, gap: 2, display: "flex", flexDirection: "column" }}>
            <AssemblyDescription description={executedReport.report.description} />

            <Divider />

            <HorizontalList items={[
                { label: "Stav", value: "Spracovanie ukončené" },
                { label: "Dátum spustenia", value: executionStartTime ? formatDate(executionStartTime) : "" },
                { label: "Dátum ukončenia", value: executionEndTime ? formatDate(executionEndTime) : "" },
                { label: "Dĺžka trvania", value: getExecutionDuration() },
            ]} />

            <Divider />

            {executedReport.errorMessage && (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography variant="body1">Popis chyby</Typography>

                        <Box sx={{
                            width: "100%",
                            overflow: "hidden",
                            background: theme.palette.grey[100],
                            padding: 2,
                            borderRadius: 1,
                        }}>
                            <Box sx={{ overflowY: "auto", maxHeight: 250 }}>
                                <Typography variant="subtitle1" fontSize={14} whiteSpace="pre-line">
                                    {executedReport.errorMessage}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Divider />
                </>
            )}

            <Typography variant="h6">Parametre, s ktorými bola zostava spustená</Typography>

            {executedReport.reportExecutionHistoryParameters.map((parameter) => (
                <ParameterInfo key={parameter.reportParameterId} {...parameter} />
            ))}
        </Box>
    );
};

const ParameterInfo = ({ values, reportParameter }: ReportExecutionHistoryParameterDto) => {
    const getValue = () => {
        if (!values.length) return "Nevyplnené";

        if (reportParameter?.type === ReportParameterTypeEnum.DATE) {
            return values.map((value) => formatDate(value.value)).join(", ");
        }

        if (reportParameter?.type === ReportParameterTypeEnum.NUMBER) {
            return values.map((value) => value.value).join(", ");
        }
    }

    return <>
        <HorizontalList items={[
            { label: "Názov parametra", value: reportParameter?.name || "Nemá názov" },
            { label: "Popis", value: reportParameter?.description || "Nemá popis" },
            { label: "Typ", value: reportParameter?.type ? getParameterTypeLabel(reportParameter?.type) : "Neznámy typ" },
            { label: "Hodnota Parametra", value: getValue() || "Nevyplnené" }
        ]} />

        <Divider />
    </>
}

export type ListItemProps = {
    label: string;
    value: string;

}

const HorizontalList = ({ items }: { items: ListItemProps[] }) => {
    const relativeWidth = `calc(${100 / items.length}% - ${(items.length - 1) * 8}px)`

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {items.map((item, index) => (
                <ListItem key={index} {...item} relativeWidth={relativeWidth} />
            ))}
        </Box>
    );
}

const ListItem = ({ label, value, relativeWidth }: ListItemProps & { relativeWidth: string; }) => {
    return (
        <Box sx={{ width: relativeWidth, minWidth: 200 }}>
            <Typography variant="body1">{label}</Typography>

            <TextOverflow variant="subtitle1" text={value} />
        </Box>
    );
}